/**
 * Mapa de rotas.
 *
 * As rotas das telas de fase futura são GERADAS a partir de NAV_ITEMS — a
 * mesma lista que monta a sidebar. Enquanto for assim, é impossível existir
 * item de menu sem rota (o erro que deixou 5 páginas órfãs no projeto anterior).
 */
import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router';

import { RequireAuth, RequireGuest } from './auth/guards';
import { NAV_ITEMS } from './features/workspace/navigation';
import { NotFoundPage } from './pages/NotFoundPage';
import { PlanosPage } from './pages/PlanosPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { RootRedirect } from './pages/RootRedirect';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { LoginPage } from './pages/auth/LoginPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { SignupPage } from './pages/auth/SignupPage';
import { VerifyEmailPage } from './pages/auth/VerifyEmailPage';
import { DashboardPage } from './pages/workspace/dashboard/DashboardPage';
import { GestaoPage } from './pages/workspace/dashboard/GestaoPage';
import { HistoricoPage } from './pages/workspace/dashboard/HistoricoPage';
import { ContatosPage } from './pages/workspace/contatos/ContatosPage';
import { RelatoriosPage } from './pages/workspace/dashboard/RelatoriosPage';
import { PhaseRoute } from './pages/workspace/PhaseRoute';
import { WorkspaceLayout } from './pages/workspace/WorkspaceLayout';
import { WorkspaceNotFound } from './pages/workspace/WorkspaceNotFound';
import { MetaAdsPage } from './pages/workspace/ads/MetaAdsPage';
import { ShopeePage } from './pages/workspace/shopee/ShopeePage';
import { NovidadesPage } from './pages/workspace/novidades/NovidadesPage';
import { CampaignsPage } from './pages/workspace/campaigns/CampaignsPage';
import { CampaignDetailPage } from './pages/workspace/campaigns/CampaignDetailPage';
import { GroupDetailPage } from './pages/workspace/groups/GroupDetailPage';
import { GroupsPage } from './pages/workspace/groups/GroupsPage';
import { IncidentsPage } from './pages/workspace/incidents/IncidentsPage';
import { MonitorsPage } from './pages/workspace/monitors/MonitorsPage';
import { RedirectorsPage } from './pages/workspace/redirectors/RedirectorsPage';
import { MembersPage } from './pages/workspace/settings/MembersPage';
import { PlanPage } from './pages/workspace/settings/PlanPage';
import { SettingsPage } from './pages/workspace/settings/SettingsPage';
import { CoursePage } from './pages/workspace/training/CoursePage';
import { GuiaCapituloPage } from './pages/workspace/training/GuiaCapituloPage';
import { GuiaIndicePage } from './pages/workspace/training/GuiaIndicePage';
import { LessonPage } from './pages/workspace/training/LessonPage';
import { TrainingPage } from './pages/workspace/training/TrainingPage';

/**
 * Vitrine do design system: carregada sob demanda, para os ~65 ícones e todas as variantes
 * não entrarem no pacote que todo usuário baixa ao abrir o painel.
 */
const DesignSystemPage = lazy(() =>
  import('./pages/design-system/DesignSystemPage').then((m) => ({ default: m.DesignSystemPage })),
);

/** Itens de menu que ainda não têm tela própria: viram rota com EmptyState. */
const PHASE_ITEMS = NAV_ITEMS.filter((item) => item.phase !== undefined);

export function AppRoutes() {
  return (
    <Routes>
      {/* Quem já tem sessão não vê login/cadastro. */}
      <Route element={<RequireGuest />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/cadastro" element={<SignupPage />} />
        <Route path="/esqueci-senha" element={<ForgotPasswordPage />} />
      </Route>

      {/*
        Estas duas NÃO passam pela guarda de visitante: quem clica no link do
        e-mail pode já estar logado em outra aba, e mandar essa pessoa para o
        painel sem processar o token deixaria a conta sem confirmar para sempre.
      */}
      {/*
        A vitrine de planos é PÚBLICA e fica fora das duas guardas: quem ainda
        não tem conta precisa ver preço antes de criar uma, e quem já entrou
        continua podendo abrir a tabela sem sair da sessão. O catálogo
        (`billing.catalog`) é o único procedure sem sessão que o painel usa.
      */}
      <Route path="/planos" element={<PlanosPage />} />

      {/*
        Vitrine do design system (`@mgdlead/ui`): PÚBLICA pelo mesmo motivo de não ter dado —
        tudo nela é exemplo fixo, e conferir um componente não pode depender de sessão.
      */}
      <Route
        path="/design-system"
        element={
          <Suspense fallback={null}>
            <DesignSystemPage />
          </Suspense>
        }
      />

      <Route path="/redefinir-senha" element={<ResetPasswordPage />} />
      <Route path="/verificar-email" element={<VerifyEmailPage />} />

      <Route element={<RequireAuth />}>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/onboarding" element={<OnboardingPage />} />

        <Route path="/w/:slug" element={<WorkspaceLayout />}>
          <Route index element={<DashboardPage />} />
          <Route path="configuracoes" element={<SettingsPage />} />
          {/*
            Fase 10: a tela de assinatura saiu de `PHASE_ITEMS` no mesmo commit em que
            `navigation.ts` perdeu o `phase` dela — mesma lista, mesmo commit, então não
            existe nem item de menu sem rota nem rota sem item de menu. O MESMO componente
            responde na aba "Plano" de `configuracoes`.
          */}
          <Route path="assinatura" element={<PlanPage />} />
          <Route path="configuracoes/membros" element={<MembersPage />} />
          <Route path="monitoradores" element={<MonitorsPage />} />
          <Route path="grupos" element={<GroupsPage />} />
          {/*
            A tela de UM grupo. Não sai de NAV_ITEMS de propósito — detalhe não é item de
            menu, é filho de um. `resolveActiveId` casa por PREFIXO, então `grupos/:groupId`
            mantém "Grupos" aceso na sidebar E na barra inferior do celular; o caminho para
            chegar aqui é o nome do grupo na lista, que é por onde a pessoa já passa.
          */}
          <Route path="grupos/:groupId" element={<GroupDetailPage />} />
          <Route path="campanhas" element={<CampaignsPage />} />
          <Route path="campanhas/:campaignId" element={<CampaignDetailPage />} />
          <Route path="redirecionadores" element={<RedirectorsPage />} />
          {/*
            Fase 8: a tela de Meta Ads saiu de `PHASE_ITEMS` no mesmo commit em que
            `navigation.ts` perdeu o `phase` dela — mesma lista, mesmo commit, então não
            existe nem item de menu sem rota nem rota sem item de menu.
          */}
          <Route path="meta-ads" element={<MetaAdsPage />} />
          {/* F1/F2 — Shopee Affiliate: conexão, importação e lucro por bloco via Sub ID. */}
          <Route path="shopee" element={<ShopeePage />} />
          {/*
            As duas telas de analytics da Fase 6. Elas saíram de `PHASE_ITEMS` no mesmo
            commit em que `navigation.ts` perdeu o `phase` delas — é a mesma lista que
            monta o menu e as rotas, então não existe item apontando para lugar nenhum.
          */}
          <Route path="analytics/gestao" element={<GestaoPage />} />
          <Route path="analytics/historico" element={<HistoricoPage />} />
          {/*
            Fase 9: a tela de relatórios saiu de `PHASE_ITEMS` no mesmo commit em que
            `navigation.ts` perdeu o `phase` dela — mesma lista, mesmo commit, então não
            existe nem item de menu sem rota nem rota sem item de menu.
          */}
          <Route path="analytics/relatorios" element={<RelatoriosPage />} />
          {/* 11/10: baixar listas e backup de contatos num lugar só. */}
          <Route path="contatos" element={<ContatosPage />} />
          {/*
            Fase 7: a tela de incidentes saiu de `PHASE_ITEMS` no mesmo commit em que
            `navigation.ts` perdeu o `phase` dela — mesma lista, mesmo commit, então não
            existe nem item de menu sem rota nem rota sem item de menu.
          */}
          <Route path="incidentes" element={<IncidentsPage />} />
          {/*
            Fase 11: as três telas de treinamento saíram de `PHASE_ITEMS` no mesmo commit
            em que `navigation.ts` perdeu o `phase` do item — mesma lista, mesmo commit,
            então não existe nem item de menu sem rota nem rota sem item de menu.

            Curso e aula NÃO saem de NAV_ITEMS de propósito: detalhe não é item de menu, é
            filho de um. `resolveActiveId` casa por PREFIXO, então `treinamentos/:curso` e
            `treinamentos/:curso/:aula` mantêm "Treinamentos" aceso na sidebar; o caminho
            para chegar até elas é o cartão do curso e a linha da aula, que é por onde a
            pessoa já passa.
          */}
          <Route path="treinamentos" element={<TrainingPage />} />
          {/*
            O Guia do MGDLead (texto + capturas, versionado em `features/guia`). Rotas
            estáticas ganham de `:courseSlug` no ranking do react-router: o guia nunca é
            confundido com um curso (e um curso de slug "guia" ficaria inacessível — não crie).
          */}
          <Route path="treinamentos/guia" element={<GuiaIndicePage />} />
          <Route path="treinamentos/guia/:capituloId" element={<GuiaCapituloPage />} />
          <Route path="treinamentos/:courseSlug" element={<CoursePage />} />
          <Route path="treinamentos/:courseSlug/:lessonSlug" element={<LessonPage />} />

          {/*
            Novidades (I5): não é item de menu — abre pelo menu da conta e pela busca (⌘K),
            como detalhe que é. A gaveta mostra as 5 mais novas; a página, todas.
          */}
          <Route path="novidades" element={<NovidadesPage />} />

          {PHASE_ITEMS.map((item) => (
            <Route key={item.id} path={item.path} element={<PhaseRoute itemId={item.id} />} />
          ))}

          <Route path="*" element={<WorkspaceNotFound />} />
        </Route>
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
